import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PermissionCode } from "@/constants/permissions";
import { Flex, HStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useRoles } from "@/hooks/useRoles";
import { AppRoutes } from "@/constants/routes";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { isEmpty } from "lodash";
import { GetFutureContractsProps } from "@/services/future-contract.service";
import {
  FutureContractEntity,
  FutureContractListResponse,
} from "./schema/future-contract.schema";
import {
  clearFiltersHandler,
  fetchFutureContractsHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { FutureContractStatus } from "@/constants/entity-status";
import { PendingMessagesAlert } from "@/components/common/section-components/alert/PendingMessagesAlert";
import { NoData } from "@/components/common/section-components/NoData";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { formatCurrency } from "@/utils/format-currency";
import { StandardTable } from "@/components/table/StandardTable";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { futureContractHeaderElements } from "./schema/header-elements";
import { FutureContractActions } from "@/components/common/section-components/action/FutureContractActions";
import { usePolling } from "@/hooks/usePolling";
import { FutureContractFilter } from "@/components/common/section-components/filters/FutureContractFilter";
import { RequestStatusIds, TipoMoneda, TipoMonedaDisplay } from "@/constants/enums";

export const FutureContractsPage = () => {
  const [isActive, setIsActive] = useState<boolean>(true);
  useEffect(() => {
    setIsActive(true);
  }, []);
  const navigate = useNavigate();
  const { userPermissions, fetchPermissions } = useRoles();

  const [page, setPage] = useState<number>(1);

  const [loadingFutureContracts, setLoadingFutureContracts] =
    useState<boolean>(false);

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [currentFilters, setCurrentFilters] = useState<
    Partial<GetFutureContractsProps> | undefined
  >();

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;

  const [futureContracts, setFutureContracts] =
    useState<FutureContractListResponse>();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.GRAIN_FUTURE_CONTRACT &&
        p.tipoPermiso === "W",
    ),
  );

  const hasReadAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.GRAIN_FUTURE_CONTRACT &&
        p.tipoPermiso === "R",
    ),
  );

  const defaultStatusFilters = [
    RequestStatusIds.AProcesar,
    RequestStatusIds.Negociacion,
    RequestStatusIds.EnComunicacion,
  ];

  const { control, handleSubmit, setValue, getValues } = useForm<FieldValues>({
    defaultValues: {
      estados: defaultStatusFilters,
    } as unknown as FieldValues,
  });

  const values = useWatch({
    control,
  });
  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const handleAdd = () => {
    navigate(AppRoutes.GRAIN_FUTURE_CONTRACTS_CREATION);
  };

  const clearFilters = () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setOpenFilters,
      setValue,
      submitFilters,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    submitFiltersHandler({
      event,
      fetchFutureContracts,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
    });
  };

  const fetchFutureContracts = async (filters?: any, showLoad = true) => {
    await fetchFutureContractsHandler({
      setLoadingFutureContracts,
      filters,
      showLoad,
      setFutureContracts,
      navigate,
      fetchPermissions,
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchFutureContracts({ ...currentFilters, "P.Pagina": page });
  };

  useEffect(() => {
    if (isActive) {
      setValue("estados", defaultStatusFilters);
      submitFiltersHandler({
        event: getValues(),
        fetchFutureContracts,
        setCurrentFilters,
        setFiltersApplied,
        setOpenFilters,
      });
    }
  }, [isActive]);

  const handleDetail = (item: FutureContractEntity) => {
    navigate(
      AppRoutes.GRAIN_FUTURE_CONTRACTS_DETAIL.replace(":id", String(item?.id)),
    );
  };

  const getActionElements = (item: FutureContractEntity) => (
    <FutureContractActions handleDetail={handleDetail} item={item} />
  );

  usePolling({
    callback: async () => {
      if (!loadingFutureContracts) await fetchFutureContracts(
        { ...currentFilters, "P.Pagina": page },
        false,
      );
      if (hasReadAccess) await fetchPermissions(false);
    },
    delay: 10000,
    isActive: isActive && !loadingFutureContracts,
  });

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        showButtons={hasWriteAccess}
        title="Venta de granos a futuro"
        loading={loadingFutureContracts}
        addButtonLabel="Agregar"
        handleAdd={handleAdd}
      />

      <HStack>
        {futureContracts?.pendingRequests && !loadingFutureContracts && (
          <PendingMessagesAlert
            submitFilters={submitFilters}
            setValue={setValue}
            label="Tiene contratos futuros pendientes de comunicación"
            statusToSet={RequestStatusIds.EnComunicacion}
          />
        )}

        {/* Si piden filtros nuevos para contrato futuro, armar componente nuevo para filtros */}
        <FutureContractFilter
          clearFilters={clearFilters}
          control={control}
          handleSubmit={handleSubmit}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          submitFilters={submitFilters}
          filtersApplied={filtersApplied}
          disableApply={filtersEmpty}
          maxDate={dayjs()}
          disableFilters={loadingFutureContracts}
        />
      </HStack>

      <Flex flexDir="column">
        {Boolean(
          !loadingFutureContracts && !futureContracts?.listado?.length,
        ) && <NoData />}

        {Boolean(
          !loadingFutureContracts && futureContracts?.listado?.length,
        ) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                futureContracts?.listado?.map((el: FutureContractEntity) => {
                  const currencySymbol = el?.tipoMoneda === TipoMoneda.ARS? "$" : "U$D ";
                  const precioFinal =
                    el?.precioFinal && el?.precioFinal > 0
                      ? `${currencySymbol}${formatCurrency(el?.precioFinal)}`
                      : "-";

                  return {
                    ...el,
                    fechaCreacion: dayjs(el?.fechaCreacion).format("DD/MM/YYYY"),
                    monto: el?.sinBase
                      ? "Sin base"
                      : `${currencySymbol}${formatCurrency(el?.monto)}`,
                    toneladas: `${el?.toneladas?.toLocaleString("es-AR")}`,
                    precioFinal,
                    tipoMoneda: TipoMonedaDisplay[el?.tipoMoneda as TipoMoneda],
                    displayEstado: (
                      <RequestStatusBadge
                        status={el?.estadoDescripcion as FutureContractStatus}
                      />
                    ),
                  };
                }) ?? []
              }
              headers={futureContractHeaderElements}
              actionElements={getActionElements}
            />
            {Number(futureContracts?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={futureContracts?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
